import type {
  FilePart,
  FileUIPart,
  ImagePart,
  ModelMessage,
  TextPart,
} from 'ai'

function extractBase64FromDataUrl(dataUrl: string): string | null {
  // Expected shape: data:<mediaType>;base64,<payload>
  const match = dataUrl.match(/^data:[^;]+;base64,([\s\S]+)$/)
  return match ? match[1] : null
}

function isTextUIPart(part: unknown): part is { type: 'text'; text: string } {
  return (
    typeof part === 'object' &&
    part !== null &&
    'type' in part &&
    part.type === 'text' &&
    'text' in part &&
    typeof part.text === 'string'
  )
}

function isFileUIPart(part: unknown): part is FileUIPart {
  return (
    typeof part === 'object' &&
    part !== null &&
    'type' in part &&
    part.type === 'file' &&
    'url' in part &&
    typeof part.url === 'string' &&
    'mediaType' in part &&
    typeof part.mediaType === 'string'
  )
}

async function urlToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      console.error(
        `[agent] Failed to fetch file from URL: ${response.statusText}`
      )
      return null
    }
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        const base64 = extractBase64FromDataUrl(result)
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('[agent] Error converting URL to base64:', error)
    return null
  }
}

async function uiFileToModelPart(
  file: FileUIPart
): Promise<ImagePart | FilePart | null> {
  const isImage = file.mediaType.startsWith('image/')

  // If it's already a data URL, extract base64
  if (file.url.startsWith('data:')) {
    const base64 = extractBase64FromDataUrl(file.url)
    if (!base64) {
      console.error('[agent] Failed to extract base64 from data URL')
      return null
    }

    return isImage
      ? ({
          type: 'image',
          image: base64,
          mediaType: file.mediaType,
        } satisfies ImagePart)
      : ({
          type: 'file',
          data: base64,
          filename: file.filename,
          mediaType: file.mediaType,
        } satisfies FilePart)
  }

  // For any other URL (blob:, http:, https:, etc.), convert to base64
  // AI models can't access signed URLs, blob URLs, or other private URLs due to CORS
  const base64 = await urlToBase64(file.url)
  if (!base64) {
    console.error('[agent] Failed to convert file URL to base64')
    return null
  }

  return isImage
    ? ({
        type: 'image',
        image: base64,
        mediaType: file.mediaType,
      } satisfies ImagePart)
    : ({
        type: 'file',
        data: base64,
        filename: file.filename,
        mediaType: file.mediaType,
      } satisfies FilePart)
}

export async function uiMessageToModelMessage(
  message: ModelMessage
): Promise<ModelMessage | null> {
  const role = (message as { role?: unknown }).role
  const content = (message as { content?: unknown }).content
  const parts =
    'parts' in message && Array.isArray((message as { parts?: unknown }).parts)
      ? ((message as unknown as { parts: unknown[] }).parts as unknown[])
      : null

  if (role === 'user') {
    if (parts) {
      const contentParts: Array<TextPart | ImagePart | FilePart> = []
      for (const part of parts) {
        if (isTextUIPart(part)) {
          contentParts.push({
            type: 'text',
            text: part.text,
          } satisfies TextPart)
          continue
        }
        if (isFileUIPart(part)) {
          const modelPart = await uiFileToModelPart(part)
          if (modelPart) {
            contentParts.push(modelPart)
          } else {
            console.error('[agent] Failed to convert file part, skipping')
          }
        }
      }

      if (contentParts.length > 0) {
        return { role: 'user', content: contentParts } as ModelMessage
      }
    }

    if (typeof content === 'string') {
      return { role: 'user', content } as ModelMessage
    }

    return null
  }

  if (role === 'assistant' || role === 'system') {
    if (typeof content === 'string' && content.length > 0) {
      return { role, content } as ModelMessage
    }
    return null
  }

  return null
}
